import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { LanguageDropdown } from '@/components/layout';
import { buttonVariants, TRANSPARENT_UNDERLINE_CLASSES } from '@/components/ui';
import { HEADER_FADE } from '@/utils/animation';
import { ClearlabWordmark } from './ClearlabWordmark';
import { cn } from '@/utils/cn';

// Mobile menu timing:
// - Pill height expansion / collapse: snappy 300ms ease-out in both directions
//   so the box itself feels responsive on tap.
// - Item fade on open: uses the shared HEADER_FADE preset (slow ease-out fade
//   shared with the home / story / technology section headers) so the menu
//   reveal feels consistent with the rest of the site. Items hold for 70% of
//   the height growth before starting to fade in.
// - Item fade on close: matches the height — 300ms ease-out, no delay — so
//   the box and its contents disappear together.
const HEIGHT_DURATION_MS = 300;
const HEIGHT_EASE = 'ease-out';
const FADE_OPEN_DURATION_MS = HEADER_FADE.durationMs;
const FADE_OPEN_EASE = HEADER_FADE.ease;
const FADE_OPEN_DELAY_MS = Math.round(HEIGHT_DURATION_MS * 0.7);
const FADE_CLOSE_DURATION_MS = 300;
const FADE_CLOSE_EASE = 'ease-out';

const NAV_LINKS = [
  { to: '/products', labelKey: 'nav.products' },
  { to: '/technology', labelKey: 'nav.technology' },
  { to: '/our-story', labelKey: 'nav.ourStory' },
  { to: '/contact', labelKey: 'nav.contact' },
] as const;

/**
 * Page-wide fixed navigation pill — pinned to the top at every scroll position.
 *
 * Mobile  : hamburger + logo + language dropdown. Tapping the hamburger expands
 *           the pill into a vertical page menu with a backdrop overlay; the
 *           hamburger morphs into an X (top + bottom lines rotate to cross at
 *           the center; middle line fades).
 * Tablet+ : same pill but with the page-link cluster overlaid above the pill bar.
 */
export function NavigationPill() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Active language is sourced from i18n; both nav-header and mobile-menu
  // pickers are views onto the same i18n.language. `changeLanguage` writes
  // the new value through to localStorage (via the language detector) so
  // the choice survives a refresh.
  const language = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const setLanguage = (next: string) => {
    void i18n.changeLanguage(next);
  };
  // Portal targets for the language pickers' open menus. Both are rendered
  // as siblings of the pill rather than as descendants because the pill
  // itself uses `backdrop-filter`, which creates a stacking context — and
  // a backdrop-filter element nested inside another backdrop-filter
  // element only sees the parent's painted output as its backdrop, which
  // is transparent outside the parent's bounds. Portaling the menus
  // outside the pill lets their backdrop-filter sample the document
  // content directly, exactly like the pill itself does.
  const [mobileLangMenuTarget, setMobileLangMenuTarget] = useState<HTMLDivElement | null>(null);
  const [desktopLangMenuTarget, setDesktopLangMenuTarget] = useState<HTMLDivElement | null>(null);
  const location = useLocation();

  // Close on route change so navigating from a menu link drops the overlay.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open — same convention as the
  // ColorModal so the page beneath the overlay doesn't drift.
  useEffect(() => {
    if (!menuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [menuOpen]);

  // Esc to close.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Close on resize past the md breakpoint. The mobile menu is hidden via
  // `md:hidden` once the viewport reaches md, but the open state — and
  // the body-scroll lock it triggers — would otherwise persist, leaving
  // the user unable to scroll the page. Tailwind's `md` is 768px.
  useEffect(() => {
    if (!menuOpen) return;
    const mq = window.matchMedia('(min-width: 768px)');
    if (mq.matches) {
      setMenuOpen(false);
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [menuOpen]);

  return (
    <>
      {/* Backdrop overlay — fades in behind the pill while the menu is open.
          Sits below the pill (z-30 vs the pill's z-40) and intercepts clicks
          to close. The blur + tint live on an inner div so the outer can
          fade smoothly via opacity without backdrop-filter "leaking" while
          collapsing. */}
      <div
        aria-hidden
        onClick={() => setMenuOpen(false)}
        className={cn(
          'fixed inset-0 z-30 transition-opacity duration-100 ease-out md:hidden',
          menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <div className="absolute inset-0 bg-clearlab-divider-mid/40 backdrop-blur-[15px]" />
      </div>

      <div className="pointer-events-none fixed inset-x-0 top-6 z-40 flex justify-center px-6">
        {/* The relative wrapper hosts the pill in flow plus two absolutely
            positioned portal targets (mobile + desktop language menus)
            anchored 8px below the pill via `top-full mt-2`. Targets being
            absolute means they don't compound with each other in flow, so
            the visible 8px gap below the pill is exact regardless of
            which menu is open at the current breakpoint. Wrapper stays
            `pointer-events-none` (inherits from the fixed parent) so the
            invisible target areas below the pill don't block clicks;
            `pointer-events-auto` is moved to the pill. */}
        <div className="relative w-full max-w-[1400px]">
          {/* Pill — flex-col now so the mobile menu can stack below the
              header row when expanded. On md+ the menu container is hidden,
              so the pill collapses back to a single header-height bar.

              Pill bg shifts to `#FAFAFA/70` when the mobile menu is open and
              eases back to the default `#edeceb/70` on close. The transition
              duration matches the mobile menu's `max-height` animation so
              the colour shift lands exactly as the box finishes
              growing / collapsing — open uses ease-in to "settle" into the
              expanded state, close uses ease-out so the colour drains in
              sync with the height collapsing back to the header row. */}
          <div
            className={cn(
              'pointer-events-auto flex flex-col rounded-lg',
              menuOpen ? 'bg-[#FAFAFA]/70' : 'bg-[#edeceb]/70',
              'shadow-pill backdrop-blur-pill',
              'transition-colors',
              menuOpen ? 'ease-in' : 'ease-out',
              // Figma node 140:1940 — uniform 16px padding on every side at
              // every breakpoint (was previously bumped to 24px on md+).
              'p-4',
            )}
            style={{ transitionDuration: `${HEIGHT_DURATION_MS}ms` }}
          >
            {/* Header row — `md:justify-between` distributes 6 flex items
                across the row at md+ (logo cluster, 4 page links, language
                dropdown), giving 5 equal gaps that grow with viewport. The
                spec: when the gaps reach 80px (around 1024px viewport),
                that's the natural matching point with the previous fixed
                page-link gap; below that, all gaps continue shrinking
                equally down to the mobile breakpoint. On mobile only the
                logo cluster is visible (page links + dropdown are
                `display: none`), so default justify (start) keeps it
                left-aligned without `justify-between`. */}
            <div className="flex w-full items-center md:justify-between">
              {/* Logo cluster. Per Figma node 140:1968 the desktop logo
                  sits inside its own 16px-padded wrapper, so the image
                  ends up 32px from the pill's outer left (16px pill
                  padding + 16px wrapper padding). On mobile this wrapper
                  also holds the hamburger; the inner gap-4 (16px)
                  matches the mobile spec, and `md:px-4` only adds the
                  Figma's logo inset where it applies. */}
              <div className="flex items-center gap-4 md:px-4">
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label={menuOpen ? t('nav.menu.close') : t('nav.menu.open')}
                  aria-expanded={menuOpen}
                  className="relative inline-flex size-6 items-center justify-center text-clearlab-midnight md:hidden"
                >
                  {/* Three CSS-animated lines that morph hamburger → X.
                      Lines are absolutely positioned so the top/bottom can
                      slide to the center (via `top`) and rotate (via
                      `transform`) on the same transition. The middle line
                      fades out so the X reads cleanly. */}
                  <span
                    className={cn(
                      'absolute left-1/2 h-0.5 w-[18px] -translate-x-1/2 bg-current transition-all duration-300 ease-out',
                      menuOpen ? 'top-[11px] rotate-45' : 'top-[5px]',
                    )}
                  />
                  <span
                    className={cn(
                      'absolute left-1/2 top-[11px] h-0.5 w-[18px] -translate-x-1/2 bg-current transition-opacity duration-300 ease-out',
                      menuOpen ? 'opacity-0' : 'opacity-100',
                    )}
                  />
                  <span
                    className={cn(
                      'absolute left-1/2 h-0.5 w-[18px] -translate-x-1/2 bg-current transition-all duration-300 ease-out',
                      menuOpen ? 'top-[11px] -rotate-45' : 'top-[17px]',
                    )}
                  />
                </button>
                <NavLink to="/" aria-label={t('nav.brand.homeLabel')} className="inline-flex">
                  <ClearlabWordmark className="h-4 w-auto" />
                </NavLink>
              </div>

              {/* Page links — `md:contents` makes the wrapping <nav>
                  invisible to layout on md+ so each NavLink becomes a
                  direct flex item of the header row alongside the logo
                  cluster and language dropdown. That's what gives the
                  6-item layout its equal-gap scaling under
                  justify-between. The <nav>'s aria-label is preserved
                  (ARIA still applies under `display: contents`). */}
              <nav aria-label={t('nav.primary')} className="hidden md:contents">
                {NAV_LINKS.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={cn(
                      buttonVariants({
                        variant: 'transparent',
                        size: 'large',
                        tone: 'black',
                      }),
                      // Override `self-start` baked into the transparent
                      // variant. That default keeps buttons left-aligned in
                      // flex-col parents (footer, mobile menu), but in the
                      // header row's flex-row + items-center it pulls links
                      // to the row's top edge. `self-auto` falls back to the
                      // parent's items-center so the links sit vertically
                      // centered alongside the logo and language picker.
                      'self-auto',
                    )}
                  >
                    <span className={TRANSPARENT_UNDERLINE_CLASSES}>{t(link.labelKey)}</span>
                  </NavLink>
                ))}
              </nav>

              {/* Desktop language picker — borderless variant matching
                  Figma node 140:1942. Fixed `w-[136px]` so "Deutsch"
                  (the longest label) fits comfortably inside the
                  16/8/16/8 padded pill and the trigger doesn't reflow
                  on selection. The wrapper sits at `justify-between`'s
                  right end (no negative margin), so the trigger's
                  outer right edge lands on the nav pill's content edge
                  per the Figma layout. `triggerClassName="py-2 gap-1"`
                  hits the figma trigger-padding spec (16px horizontal
                  from default `size="lg"` `p-4`, 8px vertical from
                  `py-2` overriding the default 16px) and reduces the
                  outer flex gap from `gap-2` (8px) to `gap-1` (4px).
                  The inner icon→label gap is also 4px (set globally on
                  the Dropdown's inner span — see Dropdown.tsx). */}
              <LanguageDropdown
                value={language}
                onChange={setLanguage}
                borderless
                size="lg"
                className="hidden w-[136px] self-auto md:block"
                triggerClassName="py-2 gap-1"
                menuPortalTarget={desktopLangMenuTarget}
                // Portaled into a target sibling of the pill so the
                // menu's `backdrop-filter` works (the pill's own
                // backdrop-filter would otherwise block it). Inside the
                // full-width target div, `ml-auto w-max` right-anchors
                // the menu to the pill's outer right edge and lets it
                // hug content (like the longest option label).
                menuWrapperClassName="ml-auto w-max"
              />
            </div>

            {/* Mobile menu — collapsible.
                  - Box height: snappy 300ms ease-out in both directions —
                    same speed open as close — so the pill grows/collapses
                    decisively on tap.
                  - Open item fade: uses the HEADER_FADE preset (800ms
                    ease-out) so the reveal shares the slow decelerating
                    feel of the site's section header fades. Items hold at
                    opacity 0 for the first 70% of the height growth (210ms)
                    before starting to fade.
                  - Close item fade: matches the height — 300ms ease-out,
                    no delay — so the pill and its contents disappear in
                    sync.
                  - `overflow-hidden` clips the items while the height
                    interpolates so they don't poke through during the open.
                  - `md:hidden` keeps this entirely off the layout from
                    tablet up; the existing centered page-link cluster
                    (below) is what shows there. */}
            <div
              className="overflow-hidden md:hidden"
              style={{
                maxHeight: menuOpen ? '500px' : '0px',
                transition: `max-height ${HEIGHT_DURATION_MS}ms ${HEIGHT_EASE}`,
              }}
            >
              <div
                className="flex flex-col pt-6"
                style={{
                  opacity: menuOpen ? 1 : 0,
                  transition: menuOpen
                    ? `opacity ${FADE_OPEN_DURATION_MS}ms ${FADE_OPEN_EASE} ${FADE_OPEN_DELAY_MS}ms`
                    : `opacity ${FADE_CLOSE_DURATION_MS}ms ${FADE_CLOSE_EASE}`,
                }}
              >
                <ul className="flex flex-col">
                  {NAV_LINKS.map((link) => (
                    <li key={link.to} className="border-b border-clearlab-divider-darker">
                      <NavLink
                        to={link.to}
                        onClick={() => setMenuOpen(false)}
                        className="block px-2 py-6 text-[16px] leading-[18px] text-clearlab-midnight transition-colors duration-fast hover:text-clearlab-blue"
                      >
                        {t(link.labelKey)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
                {/* Mobile-menu language picker — outlined variant of the
                    shared LanguageDropdown (footer chrome). 40px gap
                    (`mt-10`) above. The open-menu list is portaled into
                    `mobileLangMenuTarget` (an empty positioning anchor
                    rendered as a sibling 8px below the nav pill via the
                    parent's `gap-2`) so the menu can extend outside the
                    mobile-menu's `overflow-hidden` clip. The chrome
                    (frosted bg + shadow + rounded) lives on the menu
                    itself via `menuClassName`, so the "second pill"
                    only appears when the dropdown is open — masked
                    away by the wrapper's clip-path animation when
                    closed. Mirrors the desktop nav pattern, just full
                    nav width and left-aligned. */}
                <LanguageDropdown
                  value={language}
                  onChange={setLanguage}
                  className="mt-10"
                  triggerClassName="p-4"
                  menuPortalTarget={mobileLangMenuTarget}
                  menuClassName="border-0 bg-[#FAFAFA]/70 shadow-pill backdrop-blur-pill"
                />
              </div>
            </div>
          </div>

          {/* Language-picker portal targets — empty positioning anchors
              rendered 8px below the nav pill (`top-full mt-2` against
              the relative wrapper, whose height = the pill's height).
              The pickers' open menus portal into these divs; each menu
              carries its own frosted nav-pill chrome via the inner's
              `menuClassName`, so the "second pill" only appears when
              the dropdown opens (height-collapsed away when closed).
              Both targets sit at the same absolute position; breakpoint
              visibility (`md:hidden` vs `hidden md:block`) ensures only
              one is active at a time. */}
          {/* Empty positioning anchors for the language pickers' portaled
              menus. Not aria-hidden: the menu that portals in carries its own
              open/closed (aria-hidden + inert) state, and wrapping interactive
              content in a static aria-hidden container is an a11y violation. */}
          <div
            ref={setMobileLangMenuTarget}
            className="absolute inset-x-0 top-full mt-2 md:hidden"
          />
          <div
            ref={setDesktopLangMenuTarget}
            className="absolute inset-x-0 top-full mt-2 hidden md:block"
          />
        </div>
      </div>
    </>
  );
}
